import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Icon, Popup, Header, Checkbox } from 'semantic-ui-react';
import styled from 'styled-components';

import { Table, ButtonFilled } from 'components';
import { FundComparison } from 'actions';
import { colors } from 'utils/colors';

const MAX_SELECTED_TICKERS = 4;

export class Execute extends Component {
  getMaxCorETFs = (data) =>
    Object.keys(data.tickersValid).reduce((max, ticker) => {
      if (data.tickersValid[ticker].wsMatches) {
        if (data.tickersValid[ticker].wsMatches.length > max) {
          max = data.tickersValid[ticker].wsMatches.length;
        }
      }

      return max;
    }, 0);

  isLinked = (data) => {
    if (!data || !data.portfolio) {
      return false;
    }

    if (
      data.portfolio.portfolio_type === 'custom' ||
      data.portfolio.portfolio_type === 'quantified'
    ) {
      return true;
    }

    return data.portfolio.is_linked;
  };

  sortTicker = (data) => {
    const tickerCorEtfLenPair = Object.keys(data).reduce((result, ticker) => {
      result.push([ticker, data[ticker].wsMatches.length]);

      return result;
    }, []);

    return tickerCorEtfLenPair
      .sort((a, b) => b[1] - a[1])
      .reduce((result, ticker) => {
        result.push(ticker[0]);

        return result;
      }, []);
  };

  toggleTicker = (ticker) => {
    const { dispatch, selectedTickers } = this.props;

    if (
      !selectedTickers.includes(ticker) &&
      selectedTickers.length >= MAX_SELECTED_TICKERS
    ) {
      return;
    }

    dispatch(FundComparison.toggleSelectedTicker(ticker));
  };

  goToFundComparison = () => {
    const { dispatch, selectedTickers, history } = this.props;

    dispatch(FundComparison.clear());

    Promise.all(
      selectedTickers.map((ticker) => dispatch(FundComparison.addFund(ticker)))
    ).then(() => {
      window.scrollTo(0, 0);
      history.push('/tools/fund-comparison');
    });
  };

  renderDummyCell = (corEtfsLength, maxCorETFs) => {
    const dummyCell = [];
    for (let i = 0; i < maxCorETFs - corEtfsLength; i += 1) {
      dummyCell.push(<Table.Cell key={i} />);
    }

    return dummyCell;
  };

  render() {
    const { data, user_region, selectedTickers } = this.props;
    const maxCorETFs = this.getMaxCorETFs(data);

    return (
      <Container>
        <Header size="large" content="Execute" />
        <p>
          To execute your portfolio, you need to choose an ETF for each asset
          class, and purchase the ETFs through your broker.
        </p>
        {user_region !== 'US' && (
          <p>
            Should you choose a Canadian or U.S.-listed ETF? Read our short
            article:
            <br />
            <a
              href="https://www.wealthscope.ca/foreignwithholdingtax"
              target="_blank"
              rel="noopener noreferrer"
            >
              <strong>
                Investing Abroad - Canadian or U.S.-Listed ETFs? Some Tax
                Considerations
              </strong>
            </a>
          </p>
        )}

        <TableWrapper>
          <FundDetailsBar>
            <ButtonFilled
              color={colors.blue}
              onClick={this.goToFundComparison}
              style={{ width: '137px' }}
              disabled={selectedTickers.length === 0}
              className={selectedTickers.length === 0 ? 'disabled' : ''}
            >
              Fund Details
            </ButtonFilled>
            <p
              style={{
                color: 'rgba(0,0,0,.6)',
                fontSize: '14px',
                marginTop: '0.5rem',
              }}
            >
              {selectedTickers.length} selected - select up to{' '}
              {MAX_SELECTED_TICKERS - selectedTickers.length} more
            </p>
          </FundDetailsBar>

          <StyledDiv>
            <Table basic textAlign="center">
              <Table.Header>
                <Table.Row>
                  {(maxCorETFs
                    ? [
                        'Asset',
                        'Weight (%)',
                        'Fund Benchmark',
                        'Proxy ETF',
                        'Highly Correlated ETFs',
                      ]
                    : ['Asset', 'Weight (%)', 'Fund Benchmark', 'Proxy ETF']
                  ).map((header) => (
                    <Table.HeaderCell key={header}>
                      {header === 'Proxy ETF' ? (
                        <div>
                          Proxy <br />
                          ETF
                          <Popup
                            trigger={
                              <Icon
                                name="question circle outline"
                                style={{
                                  fontSize: '15px',
                                  verticalAlign: 'initial',
                                  color: 'dimgrey',
                                }}
                              />
                            }
                            position="top center"
                            content="The actual ETF that was used in the analytics."
                            wide
                            style={{ display: 'inline-block' }}
                          />
                        </div>
                      ) : (
                        header
                      )}
                    </Table.HeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {this.sortTicker(data.tickersValid).map((key) => (
                  <Table.Row key={key}>
                    <Table.Cell>
                      {this.isLinked(data)
                        ? data.tickersValid[key].name
                        : data.tickersValid[key].wsName}
                    </Table.Cell>
                    <Table.Cell>{`${
                      Math.round(data.tickersValid[key].weight * 10000) / 100
                    }%`}</Table.Cell>
                    <Table.Cell>{data.tickersValid[key].wsBnmk}</Table.Cell>
                    <SelectableCell
                      selected={selectedTickers.includes(key)}
                      onClick={() => this.toggleTicker(key)}
                    >
                      <CellContent>
                        <Checkbox
                          checked={selectedTickers.includes(key)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => this.toggleTicker(key)}
                        />
                        <div>
                          <strong>{key}</strong>
                          <div
                            style={{ lineHeight: '1.2rem', fontSize: '0.9rem' }}
                          >
                            {data.tickersValid[key].name}
                          </div>
                        </div>
                      </CellContent>
                    </SelectableCell>
                    {data.tickersValid[key].wsMatches.map((item) => (
                      <SelectableCell
                        key={item.matchTicker}
                        selected={selectedTickers.includes(item.matchTicker)}
                        onClick={() => this.toggleTicker(item.matchTicker)}
                      >
                        <CellContent>
                          <Checkbox
                            checked={selectedTickers.includes(item.matchTicker)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => this.toggleTicker(item.matchTicker)}
                          />
                          <div>
                            <strong>{item.matchTicker}</strong>
                            <div
                              style={{
                                lineHeight: '1.2rem',
                                fontSize: '0.9rem',
                              }}
                            >
                              {item.matchName}
                            </div>
                          </div>
                        </CellContent>
                      </SelectableCell>
                    ))}
                    {this.renderDummyCell(
                      data.tickersValid[key].wsMatches.length,
                      maxCorETFs
                    )}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </StyledDiv>
        </TableWrapper>
      </Container>
    );
  }
}

Execute.propTypes = {
  data: PropTypes.object,
  user_region: PropTypes.string,
  dispatch: PropTypes.func.isRequired,
  selectedTickers: PropTypes.array,
  history: PropTypes.object.isRequired,
};

Execute.defaultProps = {
  data: {},
  user_region: 'CA',
  selectedTickers: [],
};

export default withRouter(
  connect((state) => ({
    selectedTickers: state.FundComparison.executeTickers,
  }))(Execute)
);

const TableWrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
  max-width: 100%;
`;

const FundDetailsBar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-bottom: 1rem;
`;

const CellContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
  text-align: left;
`;

const SelectableCell = styled(Table.Cell)`
  position: relative;
  cursor: pointer;
  background-color: ${(props) =>
    props.selected ? '#f9fafb' : 'inherit'} !important;

  :hover {
    background-color: #f0f0f0 !important;
  }
`;

const StyledDiv = styled.div`
  overflow: auto;
  margin-bottom: 2rem;
  max-width: 1200px;

  th {
    padding: 1rem !important;
  }

  th,
  td {
    padding: 0.7rem;
    min-width: 120px;
    max-width: 120px;
  }

  thead tr {
    display: block;
    background: #ffedd6;
  }

  tbody {
    display: block;
    overflow: auto;
    max-height: 520px;
  }

  .table {
    width: initial;
  }
`;

const Container = styled.div`
  padding: 2rem 2rem 1rem 2rem;
`;
